// Strategy switcher state — synced to URL search param ?strategyId=N
// with localStorage fallback for cross-page persistence.
//
// Resolution order on each render:
//   1. URL ?strategyId=N (highest priority — shareable / linkable)
//   2. localStorage 'aHedge.selectedStrategyId'
//   3. undefined → server falls back to first active strategy
//
// When the user picks a different strategy from the header dropdown,
// we update BOTH the URL and localStorage so it persists across reloads
// and across pages (Overview ↔ Actions ↔ Settings).
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const LS_KEY = 'aHedge.selectedStrategyId';

export function useSelectedStrategy(): {
  selectedStrategyId: number | undefined;
  setSelectedStrategyId: (id: number | undefined) => void;
} {
  const location = useLocation();
  const navigate = useNavigate();

  const selectedStrategyId = useMemo<number | undefined>(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('strategyId');
    if (raw) {
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) return n;
    }
    if (typeof window !== 'undefined') {
      const stored = window.localStorage.getItem(LS_KEY);
      if (stored) {
        const n = Number(stored);
        if (Number.isFinite(n) && n > 0) return n;
      }
    }
    return undefined;
  }, [location.search]);

  const setSelectedStrategyId = useCallback(
    (id: number | undefined) => {
      const params = new URLSearchParams(location.search);
      if (id === undefined) {
        params.delete('strategyId');
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem(LS_KEY);
        }
      } else {
        params.set('strategyId', String(id));
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(LS_KEY, String(id));
        }
      }
      const q = params.toString();
      navigate(
        { pathname: location.pathname, search: q ? `?${q}` : '' },
        { replace: false },
      );
    },
    [location, navigate],
  );

  return { selectedStrategyId, setSelectedStrategyId };
}
